<template>
    <el-dialog
        :visible.sync="innerVisible"
        width="500px"
        :title="title"
        :show-close="true"
        :modal="false"
        :lock-scroll="true"
        append-to-body
        custom-class="source-qrcode-dialog"
        :center="true"
        @close="onClose"
    >
        <div ref="wrapperRef" class="source-qrcode-wrapper">
            <!-- 失败状态：显示错误信息 + 重新生成按钮 -->
            <div v-if="qrError" class="qrcode-error">
                <i class="el-icon-circle-close error-icon"></i>
                <div class="error-text">{{ qrError }}</div>
                <el-button
                    type="primary"
                    :loading="loading"
                    class="retry-btn"
                    @click="retryFetchQrcode"
                >重新生成</el-button>
            </div>
            <template v-else>
                <canvas ref="canvasRef" class="qrcode-canvas"></canvas>
                <div class="qrcode-tip">扫码查看农事作业过程</div>
                <div class="qrcode-fields">
                    <div class="qrcode-field">种植记录：{{ data && data.id != null ? data.id : '-' }}</div>
                    <div class="qrcode-field">作物：{{ data && data.crop != null ? data.crop : '-' }}</div>
                    <div class="qrcode-field">地块：{{ data && (data.plotName || data.plotId) != null ? (data.plotName || data.plotId) : '-' }}</div>
                </div>
                <el-button
                    type="primary"
                    :loading="downloading"
                    class="download-btn"
                    @click="downloadQrcode"
                >下载二维码</el-button>
            </template>
        </div>
    </el-dialog>
</template>

<script>
import QRCode from 'qrcode';
import html2canvas from 'html2canvas';
import { getPlantingQRCode, redirectPlantingQRCode } from '@/api/share';

export default {
    name: 'SourceQrcodeDialog',
    props: {
        // 是否显示
        visible: {
            type: Boolean,
            default: false,
        },
        // 弹窗标题
        title: {
            type: String,
            default: '溯源二维码',
        },
        // 业务数据对象（用于字段反显）
        data: {
            type: Object,
            default: () => ({}),
        },
        // 种植记录 ID（默认从 data.id 读取）
        plantingId: {
            type: [String, Number],
            default: undefined,
        },
        // 背景色（用于画布与下载图片）
        bgColor: {
            type: String,
            default: '#ffffff',
        },
    },
    data() {
        return {
            innerVisible: this.visible,
            remoteQrText: '',
            qrError: '',
            loading: false,
            downloading: false,
            _maskEl: null,
        };
    },
    computed: {
        // 实际生效的种植记录 ID：prop.plantingId 优先，否则取 data.id
        effectivePlantingId() {
            if (this.plantingId !== undefined && this.plantingId !== null && this.plantingId !== '') {
                return this.plantingId;
            }
            const id = this.data && this.data.id;
            return id === undefined || id === null ? undefined : id;
        },
        effectiveQrText() {
            return (this.remoteQrText || '').trim();
        },
    },
    watch: {
        visible: {
            immediate: true,
            handler(val) {
                this.innerVisible = val;
                if (val) {
                    this.showMask();
                    this.fetchQrTextIfNeeded().then(() => {
                        this.$nextTick(() => this.renderQrcode());
                    });
                } else {
                    this.remoteQrText = '';
                    this.qrError = '';
                    this.hideMask();
                }
            },
        },
        innerVisible(val) {
            this.$emit('update:visible', val);
            if (!val) {
                this.hideMask();
            }
        },
    },
    beforeDestroy() {
        this.hideMask();
    },
    methods: {
        showMask() {
            if (this._maskEl) return;
            const mask = document.createElement('div');
            mask.className = 'source-qrcode-mask';
            mask.addEventListener('click', this.onClose);
            document.body.appendChild(mask);
            this._maskEl = mask;
        },
        hideMask() {
            if (this._maskEl) {
                this._maskEl.removeEventListener('click', this.onClose);
                if (this._maskEl.parentNode) {
                    this._maskEl.parentNode.removeChild(this._maskEl);
                }
                this._maskEl = null;
            }
        },
        onClose() {
            this.innerVisible = false;
            this.$emit('update:visible', false);
            this.$emit('close');
        },
        /**
         * 扫码场景：调用 redirectPlantingQRCode 接口，传入 id + 加密后的 qrCode
         * 接口返回跳转路径（path / url），前端用 router.push 跳转
         * @param {string} qrCode 二维码内容
         * @param {{router?: object}} options 跳转选项
         * @returns {Promise<string>}
         */
        async redirectByQrCode(qrCode, options = {}) {
            const id = this.effectivePlantingId;
            if (!qrCode) {
                this.$message.warning('二维码内容不能为空');
                return '';
            }
            try {
                const res = await redirectPlantingQRCode({
                    qrId: qrCode,
                    plantingId: id,
                });
                const data = res?.data ?? res;
                const path = data?.path || data?.url || data?.redirect
                    || data?.data?.path || data?.data?.url;
                if (path) {
                    if (options.router && typeof options.router.push === 'function') {
                        options.router.push(path).catch?.((e) => console.error(e));
                    } else {
                        window.location.href = path;
                    }
                    this.$message.success('正在跳转…');
                    return path;
                }
                this.$message.warning('未获取到跳转路径');
                return '';
            } catch (e) {
                console.error('扫码跳转失败', e);
                this.$message.error('扫码跳转失败');
                return '';
            }
        },
        /**
         * 拉取二维码内容
         * - data 为字符串：直接作为二维码内容
         * - data 为对象：兼容取 qrText / url / qrCode
         * 失败时设置 qrError（用于显示"重新生成"按钮）
         * @param {boolean} force 是否强制重新拉取
         * @returns {Promise<void>}
         */
        async fetchQrTextIfNeeded(force = false) {
            const id = this.effectivePlantingId;
            if (id === undefined || id === null || id === '') {
                this.remoteQrText = '';
                this.qrError = '';
                return;
            }
            if (force) {
                this.remoteQrText = '';
            }
            if (this.remoteQrText) return;
            this.loading = true;
            this.qrError = '';
            try {
                const res = await getPlantingQRCode(id);
                const data = res?.data ?? res;
                let text = '';
                if (typeof data === 'string') {
                    text = data.trim();
                } else if (data && typeof data === 'object') {
                    text = (data.qrText || data.url || data.qrCode || '').toString().trim();
                }
                if (text) {
                    this.remoteQrText = text;
                } else {
                    this.qrError = (res && res.msg) || '生成二维码失败';
                    this.$message.error(this.qrError);
                }
            } catch (e) {
                console.error('获取溯源二维码内容失败', e);
                this.qrError = (e && e.response && e.response.data && e.response.data.msg)
                    || (e && e.msg)
                    || (e && e.message)
                    || '生成二维码失败';
                this.$message.error(this.qrError);
            } finally {
                this.loading = false;
            }
        },
        async retryFetchQrcode() {
            if (this.loading) return;
            await this.fetchQrTextIfNeeded(true);
            this.$nextTick(() => this.renderQrcode());
        },
        /** 在 canvas 上绘制二维码 */
        async renderQrcode() {
            const text = this.effectiveQrText;
            const canvas = this.$refs.canvasRef;
            if (!text || !canvas) return;
            try {
                await QRCode.toCanvas(canvas, text, {
                    width: 200,
                    margin: 2,
                    color: { dark: '#000000', light: this.bgColor },
                });
            } catch (e) {
                console.error('二维码渲染失败', e);
            }
        },
        /**
         * 下载二维码：用 html2canvas 对整个弹窗内容（含二维码 + 字段）截图导出
         * 一份真相：屏幕上看到什么，下载就是什么
         */
        async downloadQrcode() {
            const target = this.$refs.wrapperRef;
            if (!target) {
                this.$message.warning('暂无可下载内容');
                return;
            }
            this.downloading = true;
            try {
                const canvas = await html2canvas(target, {
                    backgroundColor: this.bgColor,
                    scale: 2,
                    useCORS: true,
                });
                const dataUrl = canvas.toDataURL('image/png');
                const a = document.createElement('a');
                a.href = dataUrl;
                a.download = `source-qrcode-${Date.now()}.png`;
                document.body.appendChild(a);
                a.click();
                document.body.removeChild(a);
                this.$message.success('已保存到本地');
            } catch (e) {
                console.error('下载失败', e);
                // 兜底：复制文本
                const text = this.effectiveQrText;
                if (text) {
                    try {
                        await navigator.clipboard.writeText(text);
                        this.$message.success('已复制到剪贴板');
                    } catch (err) {
                        this.$message.error('下载失败');
                    }
                } else {
                    this.$message.error('下载失败');
                }
            } finally {
                this.downloading = false;
            }
        },
    },
};
</script>

<style lang="scss" scoped>
.source-qrcode-wrapper {
    display: flex;
    flex-direction: column;
    align-items: center;
    text-align: center;
    background: #071150;
    padding: 16px;
}
.qrcode-canvas {
    width: 200px !important;
    height: 200px !important;
    background: #fff;
    border: 1px solid #49f0fd;
    border-radius: 4px;
}
.qrcode-fields {
    margin-top: 12px;
    align-self: center;
    text-align: left;
    padding: 0 8px;
}
.qrcode-field {
    color: #c7d6f0;
    font-size: 13px;
    line-height: 22px;
    white-space: nowrap;
    text-align: left;
}
.qrcode-tip {
    margin-top: 16px;
    color: #8fa4cd;
    font-size: 13px;
    text-align: center;
}
.qrcode-error {
    display: flex;
    flex-direction: column;
    align-items: center;
    padding: 32px 16px;

    .error-icon {
        font-size: 48px;
        color: #f56c6c;
    }

    .error-text {
        margin-top: 12px;
        color: #c7d6f0;
        font-size: 14px;
        text-align: center;
        word-break: break-all;
    }

    .retry-btn {
        margin-top: 20px;
        min-width: 120px;
    }
}
.download-btn {
    margin-top: 20px;
    min-width: 140px;
    border-radius: 4px;
    font-size: 14px;
    padding: 8px 18px;
    color: #000000;
    background: #49f0fd;
    border: 1px solid #49f0fd;

    &:hover {
        background: #5bf0fc;
        border-color: #5bf0fc;
    }
}
</style>

<style lang="scss">
// 自定义蒙层：覆盖整个视口，显示在外层弹窗之上
.source-qrcode-mask {
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    width: 100%;
    height: 100%;
    background-color: rgba(0, 0, 0, 0.6);
    z-index: 3000;
    cursor: pointer;
}

.source-qrcode-dialog {
    background: #071150;
    color: #fff;
    box-shadow: inset 0px 0px 10px 0px rgba(73, 240, 253, 0.25);
    border: 1px solid #49f0fd;
    border-radius: 4px;
    margin: 0 auto !important;
    top: 50% !important;
    transform: translateY(-50%) !important;
    z-index: 3001 !important;

    .el-dialog__header {
        padding: 16px;
        border-bottom: 1px solid #49f0fd;
        box-shadow: inset 0px -1px 5px #49f0fd;
        background: transparent;
        display: flex;
        align-items: center;
        position: relative !important;
        flex-shrink: 0;
    }

    .el-dialog__title {
        font-size: 16px;
        font-weight: 600;
        color: #fff;
        line-height: 24px;
        letter-spacing: 2px;
    }

    .el-dialog__headerbtn {
        position: absolute;
        top: 50%;
        transform: translateY(-50%);
        right: 16px;
        z-index: 1;

        .el-dialog__close {
            color: #49f0fd;
            font-size: 22px;
            font-weight: 300;

            &:hover {
                color: #5bf0fc;
            }
        }
    }

    .el-dialog__body {
        padding: 0 !important;
        color: #fff;
    }
}
</style>
